import { supabase } from '../lib/supabase';

export const customerService = {
  // Get customer name for an order (joins orders, customers, user)
  async getCustomerNameForOrder(orderId) {
    try {
      // Join orders -> customers -> user_profiles
      const { data: full_name, error } = await supabase
        .from('orders')
        .select(`
          id,
          customer:customers!inner(
            id,
            user:user_profiles!inner(
              id,
              full_name
            )
          )
        `)
        .eq('id', orderId)
        .maybeSingle();
      console.log(data);
      if (error) throw error;
      // Drill down to get the name
      return { name: data?.customer?.user?.full_name || null, error: null };
    } catch (error) {
      return { name: null, error };
    }
  },
  // Get customer profile by user ID
  async getCustomerByUserId(userId) {
    try {
      // Fetch customer record first
      const { data: customer, error } = await supabase?.from('customers')?.select('*')?.eq('user_id', userId)?.maybeSingle();

      if (error) throw error;

      if (!customer) {
        return { data: null, error: null };
      }

      // Fetch addresses by user_id to avoid dependency on Supabase schema relationship cache
      try {
        const { data: addresses, error: addrError } = await supabase?.from('addresses')?.select('*')?.eq('user_id', userId)?.order('is_primary', { ascending: false });
        if (addrError) {
          console.warn('Error fetching addresses for customer:', addrError);
          customer.addresses = [];
        } else {
          customer.addresses = addresses || [];
        }
      } catch (addrFetchErr) {
        console.warn('Unexpected error fetching addresses for customer:', addrFetchErr);
        customer.addresses = [];
      }

      return { data: customer, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get customer orders
  async getOrders(customerId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          supplier:suppliers!orders_supplier_id_fkey(
            business_name,
            profile_image,
            profile_image_alt
          ),
          order_items(
            *,
            menu_item:menu_items(
              name,
              image,
              image_alt
            )
          )
        `)?.eq('customer_id', customerId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get recommended dishes
  async getRecommendedDishes() {
    try {
      const { data, error } = await supabase?.from('menu_items')?.select(`
          *,
          supplier_id,
          supplier:suppliers!menu_items_supplier_id_fkey(
            business_name,
            rating
          )
        `)?.eq('is_available', true)?.order('rating', { ascending: false })?.limit(12);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get customer notifications
  async getNotifications(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get favorite suppliers
  async getFavoriteSuppliers() {
    try {
      const { data, error } = await supabase?.from('suppliers')?.select('*')?.eq('is_verified', true)?.order('rating', { ascending: false })?.limit(6);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Search dishes
  async searchDishes(query, cuisines = [], dietaryTags = []) {
    try {
      let queryBuilder = supabase?.from('menu_items')?.select(`
          *,
          supplier_id,
          supplier:suppliers!menu_items_supplier_id_fkey(
            business_name,
            rating
          )
        `)?.eq('is_available', true);

      if (query) {
        queryBuilder = queryBuilder?.or(`name.ilike.%${query}%,description.ilike.%${query}%`);
      }

      if (cuisines?.length > 0) {
        queryBuilder = queryBuilder?.in('cuisine', cuisines);
      }

      if (dietaryTags?.length > 0) {
        queryBuilder = queryBuilder?.contains('dietary_tags', dietaryTags);
      }

      const { data, error } = await queryBuilder?.order('rating', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update customer preferences
  async updateCustomerPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase?.from('customers')?.update({
          dietary_preferences: preferences?.dietaryRestrictions || [],
          favorite_cuisines: preferences?.cuisinePreferences || []
        })?.eq('user_id', userId)?.select()?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get addresses for a user (customer)
  async getAddresses(userId) {
    try {
      const { data, error } = await supabase?.from('addresses')?.select('*')?.eq('user_id', userId)?.order('is_primary', { ascending: false });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create a single address for a user (customer)
  async createAddress(userId, address) {
    try {
      const payload = {
        user_id: userId,
        label: address?.label ?? null,
        country: address?.country ?? null,
        state: address?.state ?? null,
        city: address?.city ?? null,
        locality: address?.locality ?? address?.street ?? null,
        pin_code: address?.pinCode ?? address?.pin_code ?? address?.zipCode ?? null,
        is_primary: address?.isPrimary ?? address?.is_primary ?? false
      };

      const { data, error } = await supabase?.from('addresses')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update a single address
  async updateAddress(addressId, address) {
    try {
      const payload = {
        label: address?.label,
        country: address?.country,
        state: address?.state,
        city: address?.city,
        locality: address?.locality ?? address?.street,
        pin_code: address?.pinCode ?? address?.pin_code ?? address?.zipCode,
        is_primary: address?.isPrimary ?? address?.is_primary
      };
      const { data, error } = await supabase?.from('addresses')?.update(payload)?.eq('id', addressId)?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete a single address
  async deleteAddress(addressId) {
    try {
      const { error } = await supabase?.from('addresses')?.delete()?.eq('id', addressId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Upsert addresses array for a user (creates, updates, deletes to match provided array)
  async updateCustomerAddresses(userId, addresses) {
    try {
      if (!userId) throw new Error('User ID is required');

      // Fetch existing addresses for this user
      const { data: existing = [], error: existingError } = await supabase?.from('addresses')?.select('id')?.eq('user_id', userId);
      if (existingError) throw existingError;

      const existingIds = existing?.map((e) => e?.id) || [];
      const incoming = Array.isArray(addresses) ? addresses : [];

      const toCreate = incoming.filter(a => !a?.id);
      const toUpdate = incoming.filter(a => a?.id);
      const incomingIds = incoming.filter(a => a?.id).map(a => a.id);
      const toDelete = existingIds.filter(id => !incomingIds.includes(id));

      // Create new addresses
      if (toCreate?.length > 0) {
        const payloads = toCreate.map((a) => ({
          user_id: userId,
          label: a?.label ?? null,
          country: a?.country ?? null,
          state: a?.state ?? null,
          city: a?.city ?? null,
          locality: a?.locality ?? a?.street ?? null,
          pin_code: a?.pinCode ?? a?.pin_code ?? a?.zipCode ?? null,
          is_primary: a?.isPrimary ?? a?.is_primary ?? false
        }));

        const { data: created, error: createErr } = await supabase?.from('addresses')?.insert(payloads)?.select();
        if (createErr) throw createErr;
      }

      // Update existing addresses
      for (const a of toUpdate) {
        const payload = {
          label: a?.label,
          country: a?.country,
          state: a?.state,
          city: a?.city,
          locality: a?.locality ?? a?.street,
          pin_code: a?.pinCode ?? a?.pin_code ?? a?.zipCode,
          is_primary: a?.isPrimary ?? a?.is_primary
        };
        const { data: updated, error: updErr } = await supabase?.from('addresses')?.update(payload)?.eq('id', a?.id)?.select()?.single();
        if (updErr) throw updErr;
      }

      // Delete removed addresses
      if (toDelete?.length > 0) {
        const { error: delErr } = await supabase?.from('addresses')?.delete()?.in('id', toDelete);
        if (delErr) throw delErr;
      }

      // Return fresh list
      const { data: refreshed, error: refErr } = await supabase?.from('addresses')?.select('*')?.eq('user_id', userId)?.order('is_primary', { ascending: false });
      if (refErr) throw refErr;

      return { data: refreshed, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },


  // Create new order
  async createOrder(orderData) {
    try {
      // Generate unique order number
      const orderNumber = `HK${Date.now()?.toString()?.slice(-8)}`;

      // Insert order
      const { data: order, error: orderError } = await supabase?.from('orders')?.insert({
          order_number: orderNumber,
          customer_id: orderData?.customerId,
          supplier_id: orderData?.supplierId,
          status: 'pending',
          delivery_type: orderData?.deliveryType,
          total_amount: orderData?.totalAmount,
          special_instructions: orderData?.specialInstructions,
          delivery_address: orderData?.deliveryAddress,
          estimated_delivery: orderData?.estimatedDelivery
        })?.select()?.single();

      if (orderError) throw orderError;

      // Insert order items
      const orderItems = orderData?.items?.map(item => ({
        order_id: order?.id,
        menu_item_id: item?.menuItemId,
        quantity: item?.quantity,
        price: item?.price
      }));

      const { error: itemsError } = await supabase?.from('order_items')?.insert(orderItems);

      if (itemsError) throw itemsError;

      return { data: order, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get cart items for a customer
  async getCartItems(customerId) {
    try {
      const { data, error } = await supabase
        ?.from('cart_items')
        ?.select(`
          *,
          menu_item:menu_items(
            *,
            supplier:suppliers!menu_items_supplier_id_fkey(
              id,
              business_name,
              profile_image,
              profile_image_alt
            )
          )
        `)
        ?.eq('customer_id', customerId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Add item to cart
  async addToCart(customerId, menuItemId, quantity = 1) {
    try {
      // Check if item already exists in cart
      const { data: existingItem, error: checkError } = await supabase
        ?.from('cart_items')
        ?.select('*')
        ?.eq('customer_id', customerId)
        ?.eq('menu_item_id', menuItemId)
        ?.maybeSingle();

      if (checkError) throw checkError;

      if (existingItem) {
        // Update quantity if item exists
        const { data, error } = await supabase
          ?.from('cart_items')
          ?.update({ quantity: existingItem?.quantity + quantity })
          ?.eq('id', existingItem?.id)
          ?.select()
          ?.single();

        if (error) throw error;
        return { data, error: null };
      } else {
        // Insert new item
        const { data, error } = await supabase
          ?.from('cart_items')
          ?.insert({ customer_id: customerId, menu_item_id: menuItemId, quantity })
          ?.select()
          ?.single();

        if (error) throw error;
        return { data, error: null };
      }
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update cart item quantity
  async updateCartItemQuantity(cartItemId, quantity) {
    try {
      const { data, error } = await supabase
        ?.from('cart_items')
        ?.update({ quantity })
        ?.eq('id', cartItemId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Remove item from cart
  async removeFromCart(cartItemId) {
    try {
      const { error } = await supabase
        ?.from('cart_items')
        ?.delete()
        ?.eq('id', cartItemId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Clear all cart items for a customer
  async clearCart(customerId) {
    try {
      const { error } = await supabase
        ?.from('cart_items')
        ?.delete()
        ?.eq('customer_id', customerId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Get cart item count
  async getCartItemCount(customerId) {
    try {
      const { count, error } = await supabase
        ?.from('cart_items')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('customer_id', customerId);

      if (error) throw error;
      return { count, error: null };
    } catch (error) {
      return { count: 0, error };
    }
  },

  // Get review for a specific order
  async getReviewForOrder(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('reviews')
        ?.select('*')
        ?.eq('order_id', orderId)
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create a review for a completed order
  async createReview(reviewData) {
    try {
      const { data, error } = await supabase
        ?.from('reviews')
        ?.insert({
          order_id: reviewData?.orderId,
          customer_id: reviewData?.customerId,
          supplier_id: reviewData?.supplierId,
          rating: reviewData?.rating,
          comment: reviewData?.comment
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update an existing review
  async updateReview(reviewId, reviewData) {
    try {
      const { data, error } = await supabase
        ?.from('reviews')
        ?.update({
          rating: reviewData?.rating,
          comment: reviewData?.comment
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};